import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeIn = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }
};

export default function SharedDecisionPage() {
  const navigate = useNavigate();
  const { shareToken } = useParams();
  const [decisionMap, setDecisionMap] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSharedDecision = async () => {
      try {
        const response = await axios.get(`${API}/shared/${shareToken}`);
        setDecisionMap(response.data);
      } catch (err) {
        setError(err.response?.status === 404 
          ? "Link expired or invalid" 
          : "Unable to load");
      } finally {
        setIsLoading(false);
      }
    };
    loadSharedDecision();
  }, [shareToken]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 bg-background">
        <Lock className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" onClick={() => navigate("/")}>
          Go to DECISORA
        </Button>
      </div>
    );
  }

  const riskClass = {
    Low: "risk-low",
    Medium: "risk-medium",
    High: "risk-high"
  }[decisionMap.risk_level] || "risk-medium";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            Read-only
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="space-y-10">
          
          {/* Decision Header */}
          <motion.div {...fadeIn} className="space-y-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Shared Decision
            </p>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground leading-snug" data-testid="decision-text">
              {decisionMap.decision_text}
            </h1>
            <p className="text-sm text-muted-foreground">
              Framework: {decisionMap.framework}
            </p>
          </motion.div>

          {/* Options Comparison */}
          <motion.div {...fadeIn} transition={{ delay: 0.05 }} className="space-y-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Options Compared
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="floating-card p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-md bg-primary/20 flex items-center justify-center text-xs font-medium text-primary">A</span>
                  <span className="text-xs text-muted-foreground">Change</span>
                </div>
                <p className="text-foreground font-medium" data-testid="option-a">
                  {decisionMap.option_a}
                </p>
                {decisionMap.option_a_tradeoffs && (
                  <p className="text-sm text-muted-foreground">
                    {decisionMap.option_a_tradeoffs}
                  </p>
                )}
              </div>
              
              <div className="floating-card p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-md bg-secondary flex items-center justify-center text-xs font-medium text-secondary-foreground">B</span>
                  <span className="text-xs text-muted-foreground">Status Quo</span>
                </div>
                <p className="text-foreground font-medium" data-testid="option-b">
                  {decisionMap.option_b}
                </p>
                {decisionMap.option_b_tradeoffs && (
                  <p className="text-sm text-muted-foreground">
                    {decisionMap.option_b_tradeoffs}
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {/* Risk & Confidence */}
          <motion.div {...fadeIn} transition={{ delay: 0.08 }} className="flex flex-wrap gap-3">
            {decisionMap.risk_level && (
              <div className="space-y-1">
                <div className={`px-4 py-2 rounded-lg text-sm font-medium ${riskClass}`} data-testid="risk-level">
                  {decisionMap.risk_level} Risk
                </div>
                {decisionMap.risk_explanation && (
                  <p className="text-xs text-muted-foreground px-1">{decisionMap.risk_explanation}</p>
                )}
              </div>
            )}
            {decisionMap.confidence_score && (
              <div className="px-4 py-2 rounded-lg bg-secondary border border-border text-sm font-medium text-foreground" data-testid="confidence-score">
                {decisionMap.confidence_score}% Confidence
              </div>
            )}
          </motion.div>

          <div className="h-px bg-border/50" />

          {/* Clarity Statement */}
          <motion.div {...fadeIn} transition={{ delay: 0.1 }} className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              What The Analysis Reveals
            </p>
            <p className="text-lg text-foreground leading-relaxed" data-testid="clarity-statement">
              {decisionMap.clarity_statement}
            </p>
          </motion.div>

          {/* Key Trade-offs */}
          <motion.div {...fadeIn} transition={{ delay: 0.12 }} className="space-y-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Trade-offs Identified
            </p>
            <ul className="space-y-3" data-testid="key-insights">
              {decisionMap.key_insights.map((insight, index) => (
                <li key={index} className="flex items-start gap-3 text-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span className="leading-relaxed">{insight}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Practical Steps */}
          {decisionMap.practical_steps && decisionMap.practical_steps.length > 0 && (
            <motion.div {...fadeIn} transition={{ delay: 0.14 }} className="glow-card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-primary uppercase tracking-wider">
                  What This Means In Practice
                </p>
                <ArrowUpRight className="h-4 w-4 text-primary/50" />
              </div>
              <ul className="space-y-3" data-testid="practical-steps">
                {decisionMap.practical_steps.map((step, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-md bg-primary/20 flex items-center justify-center text-xs font-medium text-primary flex-shrink-0 mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-foreground leading-relaxed">{step}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Closing Note */}
          <motion.div {...fadeIn} transition={{ delay: 0.16 }} className="pt-4 border-t border-border/30">
            <p className="text-sm text-muted-foreground italic leading-relaxed" data-testid="closing-note">
              {decisionMap.closing_note || "This result doesn't tell you what to do. It surfaces which path aligns more consistently with your own reasoning."}
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div {...fadeIn} transition={{ delay: 0.18 }} className="text-center pt-6 space-y-4">
            <p className="text-sm text-muted-foreground">Make your own decision</p>
            <Button
              data-testid="try-decisora-btn"
              onClick={() => navigate("/")}
              className="h-11 px-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm shadow-lg shadow-primary/20"
            >
              Try DECISORA
            </Button>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 py-8 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-3">
          <p className="text-xs text-muted-foreground">
            Business and Relationship decisions coming soon.
          </p>
          <p className="text-[11px] text-muted-foreground/50">
            Decisora provides structured thinking tools, not professional advice.
          </p>
        </div>
      </footer>
    </div>
  );
}
